import { enableDragToScroll } from "./dragToScroll.js";

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest("#shopify-section-header");
    this.headerWrapper = this.querySelector("[data-header-wrapper]");
    this.headerLoad = this.querySelector("[data-header-load]");
    this.predictiveSearch = this.querySelector("predictive-search");
    this.enableStickyHeader = [true, "true"].includes(
      this.dataset.enableStickyHeader,
    );
    this.announcementBar = document.querySelector("announcement-bar");
    this.isTransparent = this.hasAttribute("data-transparent");
    this.mobileNavExposed = this.querySelector("[data-mobile-nav-exposed]");
    this.mobileNavExposedWrapper = this.mobileNavExposed?.closest("nav");
    this.headerOffset =
      document.querySelector("#shopify-section-announcement-bar")
        ?.offsetHeight || 0;

    this.updateRootVar("--header-height", `${this.header.offsetHeight}px`);
    this.naturalHeight = this.header.offsetHeight;
    this.createPlaceholder();
    this.setHeaderOffset();
    this.setEventListeners();
  }

  /**
   * Create an in-flow spacer that reserves the header's natural (load) height
   * while the header is fixed, so the document height stays constant and the
   * page does not jump when the header compacts. Skipped for the transparent
   * index header, which is meant to overlap the hero.
   */
  createPlaceholder() {
    if (this.isTransparent) {
      return;
    }

    this.placeholder = document.createElement("div");
    this.placeholder.setAttribute("data-header-placeholder", "");
    this.placeholder.style.height = "0px";
    this.placeholder.style.display = "none";
    this.header.after(this.placeholder);
  }

  /**
   * Update :root CSS variables for announcement bar & header height.
   * @param {String} variable - Variable name, prefixed with '--'
   * @param {String} value - Value to update it to
   */
  updateRootVar(variable, value) {
    const root = document.querySelector(":root");
    root.style.setProperty(variable, value);
  }

  /**
   * Set event listeners.
   */
  setEventListeners() {
    this.onScrollHandler = this.onScroll.bind(this);
    this.onResizeHandler = this.onResize.bind(this);

    window.addEventListener("scroll", this.onScrollHandler, false);
    window.addEventListener("resize", this.onResizeHandler);

    if (this.mobileNavExposed) {
      enableDragToScroll(this.mobileNavExposed);
    }
  }

  /**
   * Re-measure the header's natural (load) height on resize while it is not
   * sticky, so the reserved placeholder space stays correct across breakpoints.
   */
  onResize() {
    if (!this.header.classList.contains("shopify-section-header-sticky")) {
      this.naturalHeight = this.header.offsetHeight;
    }

    this.setHeaderOffset();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
    window.removeEventListener("resize", this.onResizeHandler);
    this.placeholder?.remove();
  }

  setHeaderOffset() {
    if (!this.isTransparent) {
      return;
    }

    let offset = 0;
    const headerInnerHeight = this.headerLoad?.clientHeight || 0;
    const mobileNavExposedHeight = this.mobileNavExposed?.clientHeight || 0;
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;

    offset += headerInnerHeight;
    offset += mobileNavExposedHeight;

    if (isAnnouncementBelow && this.announcementBar) {
      offset += this.announcementBar.clientHeight;
      this.announcementBar.style.top = `${offset}px`;
    }

    this.header.style.marginBottom = `-${offset}px`;
    this.mobileNavExposedWrapper?.style.setProperty("top", `${offset}px`);
  }

  onScroll() {
    if (
      !this.enableStickyHeader ||
      this.header.classList.contains("menu-open")
    ) {
      return;
    }

    let bodyTopOffset =
      parseInt(document.body.style.top) === NaN
        ? 0
        : parseInt(document.body.style.top);
    let scrollTop = 0;
    if (bodyTopOffset !== NaN && bodyTopOffset < 0) {
      scrollTop = bodyTopOffset * -1;
    } else {
      scrollTop = window.scrollY;
    }

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    let isScrolledAfter = scrollTop > this.headerOffset;
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;

    isAnnouncementBelow
      ? (isScrolledAfter = scrollTop > 0)
      : (isScrolledAfter = scrollTop > this.headerOffset);

    this.setScrolledState(isScrolledAfter);

    if (isScrolledAfter) {
      requestAnimationFrame(this.reveal.bind(this));
    } else {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.classList.toggle("is-solid", isScrolledAfter && this.isTransparent);
  }

  setScrolledState(isScrolled) {
    this.toggleAttribute("data-scrolled", isScrolled);
    this.header?.toggleAttribute("data-header-scrolled", isScrolled);
  }

  reveal() {
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");

    if (this.placeholder) {
      this.placeholder.style.height = `${this.naturalHeight}px`;
      this.placeholder.style.display = "block";
    }

    if (this.announcementBar && !this.isTransparent) {
      this.updateRootVar("--announcement-bar-height", "0px");
      this.updateRootVar("--header-height", `${this.header.offsetHeight}px`);
    }
  }

  reset() {
    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate",
    );

    if (this.placeholder) {
      this.placeholder.style.height = "0px";
      this.placeholder.style.display = "none";
    }

    if (this.announcementBar) {
      this.updateRootVar(
        "--announcement-bar-height",
        `${this.announcementBar.offsetHeight}px`,
      );
      this.updateRootVar("--header-height", `${this.header.offsetHeight}px`);
    }
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("details-disclosure");
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector("details-modal");

    if (!this.searchModal) {
      return;
    }

    this.searchModal.close(false);
  }
}

customElements.define("sticky-header", StickyHeader);
